import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/apiMode';
import { useTranslation } from 'react-i18next';
import { HelpCircle, ChevronDown, ChevronUp, Send } from 'lucide-react';

export default function PartnerFAQ() {
  const { token } = useApp();
  const { t, i18n } = useTranslation();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [question, setQuestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    if (!token) return;
    const lang = i18n.language ? i18n.language.slice(0, 2) : 'en';
    api.get(`/api/partner/faq?lang=${lang}`)
      .then(r => setFaqs(r.data || []))
      .catch(err => setError(err.response?.data?.error || 'Errore caricamento FAQ'))
      .finally(() => setLoading(false));
  }, [token, i18n.language]);

  const handleSubmitQuestion = async () => {
    if (!question.trim()) return;
    setSubmitting(true);
    try {
      await api.post('/api/partner/faq/question', { question });
      setSubmitted(true);
      setQuestion('');
    } catch (err) {
      alert(err.response?.data?.error || 'Errore invio domanda');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 780, margin: '0 auto', padding: '1.5rem 1rem' }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ margin: 0, color: '#1e3a8a', fontSize: '1.5rem', fontWeight: 800 }}>
          {t('partner.faq.title')}
        </h1>
        <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.9rem' }}>
          {t('partner.faq.subtitle')}
        </p>
      </div>

      {error && (
        <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem', border: '1px solid #fecaca' }}>
          {error}
        </div>
      )}

      {/* FAQ list */}
      {loading ? (
        <div style={{ color: '#64748b' }}>{t('common.loading')}</div>
      ) : faqs.length === 0 ? (
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '2.5rem', textAlign: 'center', color: '#64748b', marginBottom: '1.5rem' }}>
          <HelpCircle size={40} style={{ opacity: 0.3, marginBottom: '1rem' }} />
          <p>{t('partner.faq.noFaq')}</p>
        </div>
      ) : (
        <div style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {faqs.map(faq => (
            <div
              key={faq.id}
              style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}
            >
              <button
                onClick={() => setOpenId(openId === faq.id ? null : faq.id)}
                style={{ width: '100%', textAlign: 'left', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}
              >
                <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem', lineHeight: 1.4 }}>{faq.question}</span>
                {openId === faq.id ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
              </button>
              {openId === faq.id && (
                <div style={{ padding: '0 1.25rem 1.1rem', color: '#334155', fontSize: '0.9rem', lineHeight: 1.7, borderTop: '1px solid #f1f5f9' }}>
                  <div style={{ paddingTop: '0.75rem' }}>{faq.answer}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Ask a question */}
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem', color: '#1e3a8a', fontSize: '1rem', fontWeight: 700 }}>
          {t('partner.faq.askQuestion')}
        </h3>

        {submitted ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '1rem', color: '#15803d', fontWeight: 600 }}>
            {t('partner.faq.submitted')}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder={t('partner.faq.questionPlaceholder')}
              rows={3}
              style={{ width: '100%', padding: '0.75rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
            <button
              onClick={handleSubmitQuestion}
              disabled={submitting || !question.trim()}
              style={{ alignSelf: 'flex-end', display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#1d4ed8', color: 'white', border: 'none', borderRadius: 8, padding: '0.55rem 1.1rem', fontWeight: 700, cursor: 'pointer', fontSize: '0.87rem', opacity: (!question.trim() || submitting) ? 0.6 : 1 }}
            >
              <Send size={15} /> {submitting ? '...' : t('partner.faq.submitQuestion')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
